import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface UseKeyboardShortcutsOptions {
  onToggleHelp: () => void;
}

/**
 * Centralized keyboard shortcuts for Scholar Vision.
 *
 * Shortcuts:
 *   /  or Ctrl+K  — Focus search (navigates to /browse if needed)
 *   S             — Save scholarship (on detail page only)
 *   ?             — Toggle shortcuts help overlay
 *   G then H      — Go Home
 *   G then B      — Go Browse
 *   G then M      — Go My Scholarships
 *   G then E      — Go Essays
 *   G then P      — Go Profile
 *   Escape        — Close help overlay
 *
 * All shortcuts are suppressed when focus is inside an input, textarea,
 * select, or contentEditable element.
 */
export function useKeyboardShortcuts({ onToggleHelp }: UseKeyboardShortcutsOptions) {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingG = useRef(false);
  const gTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isInputFocused = useCallback((e: KeyboardEvent): boolean => {
    const target = e.target as HTMLElement;
    if (!target) return false;
    const tag = target.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    if (target.isContentEditable) return true;
    return false;
  }, []);

  const clearG = useCallback(() => {
    pendingG.current = false;
    if (gTimer.current) {
      clearTimeout(gTimer.current);
      gTimer.current = null;
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K / Cmd+K always works (even in inputs) — standard search shortcut
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (location.pathname === '/browse') {
          window.dispatchEvent(new CustomEvent('sv-focus-search'));
        } else {
          navigate('/browse?focus=search');
        }
        return;
      }

      // All other shortcuts suppressed when typing in inputs
      if (isInputFocused(e)) return;

      // Handle G-prefix sequences
      if (pendingG.current) {
        clearG();
        const key = e.key.toLowerCase();
        const navMap: Record<string, string> = {
          h: '/',
          b: '/browse',
          m: '/journey',
          e: '/essays',
          p: '/my-profile',
        };
        if (navMap[key]) {
          e.preventDefault();
          navigate(navMap[key]);
        }
        return;
      }

      switch (e.key) {
        case '/': {
          e.preventDefault();
          if (location.pathname === '/browse') {
            window.dispatchEvent(new CustomEvent('sv-focus-search'));
          } else {
            navigate('/browse?focus=search');
          }
          break;
        }

        case '?': {
          e.preventDefault();
          onToggleHelp();
          break;
        }

        case 's': {
          // Only on scholarship detail pages
          if (location.pathname.startsWith('/scholarship/')) {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent('sv-save-scholarship'));
          }
          break;
        }

        case 'g': {
          e.preventDefault();
          pendingG.current = true;
          gTimer.current = setTimeout(() => {
            pendingG.current = false;
          }, 500);
          break;
        }

        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearG();
    };
  }, [navigate, location.pathname, onToggleHelp, isInputFocused, clearG]);
}
